import { Box, Paper, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import {
  blue,
  cyan,
  green,
  indigo,
  orange,
  pink,
  purple,
  red,
  teal,
  yellow,
} from "@mui/material/colors";
import { DefaultizedPieValueType } from "@mui/x-charts";
import DonutChart from "components/Dashboard/DonutChart";
import { WasteSubmissionsStaffSummaryQuery } from "generated-graphql/graphql";
import { useMemo } from "react";

type Props = {
  data?: WasteSubmissionsStaffSummaryQuery["wasteSubmissionsStaffSummary"];
  loading: boolean;
  onTenantClick?: (tenantName: string) => void;
  selectedTenantName?: string | null;
};

// Color palette for tenants (cycling through colors)
const TENANT_COLORS = [
  blue[400],
  green[400],
  orange[400],
  purple[400],
  red[400],
  teal[400],
  pink[400],
  cyan[400],
  yellow[600],
  indigo[400],
];

const TENANT_COLORS_INACTIVE = [
  blue[100],
  green[100],
  orange[100],
  purple[100],
  red[100],
  teal[100],
  pink[100],
  cyan[100],
  yellow[100],
  indigo[100],
];

export function TenantDonutChart({ data, loading, onTenantClick, selectedTenantName }: Props) {
  const theme = useTheme();

  const hasSelection = selectedTenantName != null;

  const tenants = useMemo(() => {
    if (!data) return [];
    return data.byTenant;
  }, [data]);

  const selectedIndex = useMemo(() => {
    if (!selectedTenantName) return null;
    const index = tenants.findIndex((t) => t.tenantName === selectedTenantName);
    return index >= 0 ? index : null;
  }, [selectedTenantName, tenants]);

  const totalSubmissions = useMemo(() => {
    if (!data) return 0;

    if (hasSelection && selectedTenantName) {
      const tenantData = tenants.find((t) => t.tenantName === selectedTenantName);
      return tenantData?.count ?? 0;
    }

    return data.total;
  }, [data, hasSelection, selectedTenantName, tenants]);

  const seriesData = useMemo(() => {
    if (!tenants.length) return [];

    return tenants.map((tenant, index) => {
      const isSelected = !hasSelection || selectedTenantName === tenant.tenantName;
      const colorIndex = index % TENANT_COLORS.length;

      return {
        // Use tenantId as unique ID (tenantName may have duplicates)
        // but store tenantName for filtering since that's what onTenantClick expects
        id: tenant.tenantId ?? `tenant-${index}`,
        tenantName: tenant.tenantName,
        value: tenant.count,
        color: isSelected ? TENANT_COLORS[colorIndex] : TENANT_COLORS_INACTIVE[colorIndex],
        label: tenant.tenantName,
      };
    });
  }, [tenants, hasSelection, selectedTenantName]);

  const legendItems = useMemo(() => {
    return seriesData.map((item, index) => {
      const isSelected = !hasSelection || selectedTenantName === item.tenantName;
      const colorIndex = index % TENANT_COLORS.length;

      return {
        color: TENANT_COLORS[colorIndex],
        isSelected,
        value: (
          <Box
            display="flex"
            width="100%"
            justifyContent="space-between"
            gap={2}
            sx={{ opacity: isSelected ? 1 : 0.4 }}
          >
            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
              {item.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.value.toLocaleString()}
            </Typography>
          </Box>
        ),
      };
    });
  }, [seriesData, hasSelection, selectedTenantName]);

  const handleTenantClick = (item: DefaultizedPieValueType) => {
    if (onTenantClick) {
      // Use tenantName for filtering (stored as custom property on the item)
      const tenantName = (item as any).tenantName ?? item.label;
      onTenantClick(tenantName as string);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        padding: 3,
        height: "100%",
        minHeight: 380,
      }}
    >
      {loading ? (
        <Box
          sx={{
            height: "100%",
            animation: "fadeIn 0.3s ease-in-out",
            "@keyframes fadeIn": {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          <Typography variant="body1" sx={{ mb: 3, color: theme.palette.text.secondary }}>
            Top Organizations
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "calc(100% - 60px)",
              gap: 4,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 250,
                height: 250,
                flexShrink: 0,
              }}
            >
              <Skeleton
                variant="circular"
                width={250}
                height={250}
                sx={{
                  animation: "pulse 1.5s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%": { opacity: 1 },
                    "50%": { opacity: 0.4 },
                    "100%": { opacity: 1 },
                  },
                }}
              />
            </Box>
            <Stack spacing={1} sx={{ flex: 1 }}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    animation: `slideIn 0.3s ease-out ${index * 0.05}s both`,
                    "@keyframes slideIn": {
                      from: {
                        opacity: 0,
                        transform: "translateX(-10px)",
                      },
                      to: {
                        opacity: 1,
                        transform: "translateX(0)",
                      },
                    },
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width={16}
                    height={16}
                    sx={{ borderRadius: "2px", flexShrink: 0 }}
                  />
                  <Skeleton variant="text" sx={{ flex: 1 }} height={20} />
                  <Skeleton variant="text" width={40} height={20} />
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            height: "100%",
            animation: "fadeIn 0.5s ease-in-out",
            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "translateY(10px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Typography variant="body1" sx={{ mb: 3, color: theme.palette.text.secondary }}>
            Top Organizations
          </Typography>
          {tenants.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={4}>
              No data available
            </Typography>
          ) : (
            <DonutChart
              seriesData={seriesData}
              selectedIndex={selectedIndex}
              label={totalSubmissions.toLocaleString()}
              subLabel={hasSelection ? "Submissions" : "Total"}
              containerHeight="18rem"
              legend={{
                direction: "column",
                position: "right",
              }}
              legendItems={legendItems}
              onItemClick={(event, pieItemIdentifier, item) => handleTenantClick(item)}
              onLegendItemClick={(item) => handleTenantClick(item)}
            />
          )}
        </Box>
      )}
    </Paper>
  );
}
